import {
	Icon,
	ICON_AGENDA,
	ICON_APPS,
	ICON_BRAIN,
	ICON_CONCLUSION,
	ICON_DOWNLOAD,
	ICON_EMAIL,
	ICON_ERROR,
	ICON_GOAL,
	ICON_HAPPY,
	ICON_IMAGE,
	ICON_LABEL,
	ICON_MAGIC_LIGHTBULB,
	ICON_MUSIC,
	ICON_OFFICE,
	ICON_POSTER,
	ICON_RATE,
	ICON_SUN,
	ICON_SWOT,
	ICON_TIME,
	ICON_USERS,
	ICON_WIZARD_HAT
} from "../../icons";
import { CardPaginationItem } from "../atoms/card-pagination";

export interface FeatureCardItem extends CardPaginationItem {
	id: string;
	name: string;
	icon: Icon;
	hidden?: boolean;
	data: {
		text: string;
		illustration?: string;
		soon?: boolean;
	};
}

export const CORE_FEATURES: FeatureCardItem[] = [
	{
		id: "workspaces",
		icon: ICON_OFFICE,
		name: "Workspaces",
		data: {
			illustration: "workspaces.mp4",
			text: "Customize your creative area with Workspaces. This feature allows you to create and manage multiple virtual spaces, each tailored to specific projects or themes, ensuring an organized and focused brainstorming environment."
		}
	},
	{
		id: "rooms",
		icon: ICON_POSTER,
		name: "Rooms",
		data: {
			illustration: "rooms.png",
			text: "Connect and collaborate in real-time with Rooms. This feature provides dedicated virtual meeting spaces for teams to gather, share ideas, and work together interactively, enhancing teamwork and creative synergy."
		}
	},
	{
		id: "share-and-embed",
		icon: ICON_EMAIL,
		name: "Share and Embed",
		data: {
			illustration: "share-and-embed.mp4",
			text: "Easily disseminate your ideas with the Share and Embed feature. This tool allows you to share your brainstorming sessions and embed them in other platforms, ensuring your creative concepts reach your audience effectively."
		}
	},
	{
		id: "import-and-export",
		icon: ICON_DOWNLOAD,
		name: "Import and Export",
		data: {
			illustration: "import-and-export.mp4",
			text: "Flexibility at your fingertips with Import and Export. This feature allows you to easily import data from other sources for brainstorming or export your ideas and sessions for use in other applications, providing versatility and convenience."
		}
	},
	{
		id: "integrations",
		icon: ICON_APPS,
		name: "Integrations",
		data: {
			illustration: "integrations.png",
			soon: true,
			text: "Streamline your workflow with Integrations. This feature enables you to connect the app with other tools and services, allowing for a more efficient and cohesive brainstorming and project management experience."
		}
	},
	{
		id: "teams-app",
		icon: ICON_USERS,
		name: "Teams App",
		data: {
			illustration: "teams-app.png",
			text: "Enhance team collaboration with the Teams App. This feature integrates seamlessly into your team's workflow, offering tools for communication, project management, and idea sharing, all in one convenient app."
		}
	}
];

export const AI_FEATURES: FeatureCardItem[] = [
	{
		id: "ai-idea-generation",
		icon: ICON_MAGIC_LIGHTBULB,
		name: "Idea Generation",
		data: {
			illustration: "ai-idea-generation.mp4",
			text: "Unleash your creativity with AI Idea Generation. Our innovative tool uses advanced algorithms to generate unique and inspiring ideas, sparking your imagination and helping you overcome creative blocks."
		}
	},
	{
		id: "ai-copilots",
		icon: ICON_WIZARD_HAT,
		name: "Copilots",
		data: {
			illustration: "copilots.mp4",
			text: "Navigate your projects seamlessly with AI Copilots. These intelligent assistants provide real-time guidance and support, helping you stay on track and efficiently manage your tasks."
		}
	},
	{
		id: "ai-topic-detection",
		icon: ICON_LABEL,
		name: "Topic Detection",
		data: {
			illustration: "topic-detection.mp4",
			text: "Discover hidden insights with AI Topic Detection. This feature intelligently analyzes content to identify and categorize key topics, making it easier to organize and understand your data."
		}
	},
	{
		id: "ai-framework-templates",
		icon: ICON_BRAIN,
		name: "Framework Templates",
		data: {
			illustration: "ai-framework-templates.mp4",
			text: "Elevate your strategic planning with AI Framework Templates. These customizable templates are powered by AI to provide structured and effective frameworks for a variety of projects and initiatives."
		}
	},
	{
		id: "duplicate-detection",
		icon: ICON_ERROR,
		name: "Duplicate Detection",
		data: {
			illustration: "duplicate-detection.png",
			soon: true,
			text: "Ensure originality and accuracy with our Duplicate Detection feature. It quickly identifies and alerts you to potential duplications in your content, safeguarding the uniqueness of your work."
		}
	},
	{
		id: "ai-challenge-suggestions",
		icon: ICON_SWOT,
		name: "Challenge Suggestions",
		data: {
			illustration: "ai-challenge-suggestions.mp4",
			text: "Get suggestions on what challenges to tackle with AI Challenge Suggestions. This feature uses AI to analyze your data and identify potential challenges, helping you to focus your efforts and maximize your impact."
		}
	},
	{
		id: "ai-inspiration",
		icon: ICON_SUN,
		name: "Inspiration",
		data: {
			illustration: "ai-inspiration.mp4",
			text: "Let our AI inspire you to come up with new ideas. This feature uses AI to analyze your challenge and helps you to overcome creative blocks and find new perspectives by asking questions."
		}
	},
	{
		id: "ai-visualization",
		icon: ICON_IMAGE,
		name: "Visualize Ideas",
		data: {
			illustration: "ai-visualization.mp4",
			text: "Bring your ideas to life with AI Visualization. This feature uses AI to transform your ideas into visual representations, making it easier to understand and communicate your concepts."
		}
	}
];

export const FACILITATE_FEATURES: FeatureCardItem[] = [
	{
		id: "agenda",
		icon: ICON_AGENDA,
		name: "Agenda",
		data: {
			illustration: "agenda.png",
			soon: true,
			text: "Effortlessly structure your meetings with the 'Agenda' feature. Set topics, allocate time for each discussion, and keep your sessions focused and productive."
		}
	},
	{
		id: "timer",
		icon: ICON_TIME,
		name: "Timer",
		data: {
			illustration: "timer.mp4",
			text: "Keep track of time during brainstorming sessions with our intuitive 'Timer' feature. Set countdowns for each agenda item to ensure efficient use of time and avoid overrun."
		}
	},
	{
		id: "goal",
		icon: ICON_GOAL,
		name: "Goal",
		data: {
			illustration: "goal.png",
			soon: true,
			text: "Set clear objectives for your brainstorming sessions with the 'Goal' feature. Define your goals and share them with participants to ensure everyone is on the same page and working towards the same outcome."
		}
	},
	{
		id: "music",
		icon: ICON_MUSIC,
		name: "Music",
		data: {
			illustration: "music.png",
			soon: true,
			text: "Enhance your brainstorming sessions with the 'Music' feature. Choose from a variety of background music to set the mood and inspire creativity while you brainstorm."
		}
	},
	{
		id: "voting",
		icon: ICON_RATE,
		name: "Voting",
		data: {
			illustration: "voting.mp4",
			text: "Harness the power of group decision-making with 'Voting'. Enable participants to vote on ideas, helping to prioritize tasks and reach consensus quickly and democratically."
		}
	},
	{
		id: "summary",
		icon: ICON_CONCLUSION,
		name: "Summary",
		data: {
			illustration: "ai-summary.mp4",
			text: "Wrap up your brainstorming sessions effectively with the 'Conclusion' feature. Find the best ideas, summarize your discussions, and set next steps to ensure your sessions are productive and impactful."
		}
	},
	{
		id: "guests",
		icon: ICON_HAPPY,
		name: "Guests",
		hidden: true,
		data: {
			text: "Invite guests to your brainstorming sessions with the 'Guests' feature. This tool allows you to share your sessions with external participants, ensuring everyone can contribute to the creative process.",
			illustration: "features.png"
		}
	}
];

export interface FeatureCollection {
	name: string;
	items: FeatureCardItem[];
}

export const ALL_FEATURES: FeatureCollection[] = [
	{
		name: "Core Features",
		items: CORE_FEATURES
	},
	{
		name: "AI Features",
		items: AI_FEATURES
	},
	{
		name: "Facilitate Features",
		items: FACILITATE_FEATURES
	}
];
